import { useCallback, useEffect, useMemo, useState } from "react";

import { useHoverLegendWriter } from "atoms/interaction";

import { useUserEvents } from "hooks";

import { LegendItemModel } from "models";
import { EntityKind } from "models/entityKind";

import { useProjectContext } from "components/project/projects/context";
import { useSelectedProject } from "components/project/projects/hooks";
import { useLoadShapefileColumn } from "components/project/shapefiles/hooks/useLoadShapefileColumn";
import { useUserDefaults } from "components/user/hooks";

import { BatchAction, GroupByFilterControllerResult } from "../types";
import { checkedBinCount, checkedValueTitles } from "../util/checkedBins";
import { useActiveFilterController } from "./useActiveFilterController";
import { useBinSettingsController } from "./useBinSettingsController";
import { useCheckedItemFilteringController } from "./useCheckedItemFilteringController";
import { useColorLockController } from "./useColorLockController";
import { useFocusSelectionController } from "./useFocusSelectionController";
import { useForecastFocusController } from "./useForecastFocusController";
import { useGroupByFilterDependencies } from "./useGroupByFilterDependencies";
import { useGroupByFilterEntities } from "./useGroupByFilterEntities";
import { useLegendController } from "./useLegendController";
import { useSavedFiltersController } from "./useSavedFiltersController";
import { useSessionBinStorage } from "./useSessionBinStorage";

// EVA-6412 SR-004 (T038/T039, narrowed in the P1 thin-controller follow-up) —
// THIN wiring layer. Composes deps + entities + the domain hooks, threads each
// hook's outputs into the ones that need them, and returns ONE result object
// for the view. Every feature's handlers/effects now live in its OWNING domain
// hook (useFocusSelectionController for groupBy selection; Checked-Item
// Filtering for the batch/lock/uncheck orchestration, including
// performBatchOption/addSelectedLegendItemsToFilter/checkOnlySelected/
// contextMenuItems/onBinsChanged/onDateRangeChanged — see CONTEXT.md for why
// that coordination now lives inside one module instead of across three).
// This file holds ONLY: hook composition/wiring, handleLegendCheckChange
// (genuinely straddles checked-items + saved-filters + bin-settings), and the
// P6 effect (genuinely cross-cutting: hasCheckedItems + showGroupsNotInFilter
// + hasEmptyBin span legend + deps).
export const useGroupByFilterController = (
  entityKind: EntityKind
): GroupByFilterControllerResult => {
  const scheduleHoverWrite = useHoverLegendWriter();
  const defaults = useUserDefaults();
  const { sendGlobalGroupByChangedEvent } = useUserEvents();
  const { permissions: projectPermissions, selectedProjectId } = useProjectContext();
  const { selectedProject } = useSelectedProject();
  const loadShapefileColumn = useLoadShapefileColumn(entityKind);

  const deps = useGroupByFilterDependencies(entityKind);
  const entities = useGroupByFilterEntities();
  const session = useSessionBinStorage();
  const forecast = useForecastFocusController();
  const colorLock = useColorLockController(deps);

  // Checked-Item Filtering is composed first: it owns checkedLegendItemsRef
  // and the stable onBinsChanged/onDateRangeChanged handlers that Bin
  // Settings/Legend call directly (ordinary composed inputs — no ref
  // trampoline). It in turn reads Legend's filtered list through its own
  // private latest-accessor (reportFilteredItems) and observes Bin Settings'
  // lock via autoLockRequestId, so no hook needs another's output before it
  // exists in this render.
  const checkedItems = useCheckedItemFilteringController(deps, {
    groupBy: deps.groupBy,
    wellListFilter: deps.wellListFilter
  });

  const binSettings = useBinSettingsController({
    deps,
    session,
    showGroupBy: entities.showGroupBy,
    setShowGroupBy: entities.setShowGroupBy,
    checkedLegendItemsRef: checkedItems.checkedLegendItemsRef,
    onBinsChanged: checkedItems.onBinsChanged,
    autoLockRequestId: checkedItems.autoLockRequestId,
    autoLockCheckedCount: checkedItems.autoLockCheckedCount
  });

  const legend = useLegendController({
    deps,
    entityKind,
    isForecastToggleOn: forecast.isForecastToggleOn,
    selectedForecastFolder: forecast.selectedForecastFolder,
    checkedLegendItemsRef: checkedItems.checkedLegendItemsRef,
    replacePlayChecked: checkedItems.replacePlayChecked,
    onDateRangeChanged: checkedItems.onDateRangeChanged,
    reportFilteredItems: checkedItems.reportFilteredItems,
    lockedColorsInStore: colorLock.lockedColorsInStore,
    lastSeenDateRangeByPropertyRef: entities.lastSeenDateRangeByPropertyRef,
    onEnterSelect: () => checkedItems.performBatchOption("checkAll"),
    showTreeMap: entities.showTreeMap
  });

  const savedFiltersController = useSavedFiltersController({
    deps,
    selectedProjectId,
    session,
    checkedLegendItemsRef: checkedItems.checkedLegendItemsRef,
    defaultFiltersLoaded: entities.defaultFiltersLoaded,
    setDefaultFiltersLoaded: entities.setDefaultFiltersLoaded,
    readyForDefaultFilter: entities.readyForDefaultFilter,
    setReadyForDefaultFilter: entities.setReadyForDefaultFilter,
    applyRestoredBin: binSettings.applyRestoredBin,
    canEditFilters: !!projectPermissions?.canEditFilters
  });

  const activeFilter = useActiveFilterController({
    deps,
    checkedLegendItemsRef: checkedItems.checkedLegendItemsRef,
    setIsForecastToggleOn: forecast.setIsForecastToggleOn,
    setSelectedForecastFolder: forecast.setSelectedForecastFolder,
    localPerformanceDateRangeRef: session.localPerformanceDateRangeRef,
    updateField: binSettings.updateField,
    defaultBinSettings: defaults?.binSettings
  });

  const { handlePresetSelection, handleRecentFocusItemClick, handleGroupByChange } =
    useFocusSelectionController({
      deps,
      entityKind,
      isForecastToggleOn: forecast.isForecastToggleOn,
      setIsForecastToggleOn: forecast.setIsForecastToggleOn,
      selectedForecastFolder: forecast.selectedForecastFolder,
      setSelectedForecastFolder: forecast.setSelectedForecastFolder,
      setCumPlusForecast: forecast.setCumPlusForecast,
      loadShapefileColumn,
      sendGlobalGroupByChangedEvent,
      scheduleHoverWrite,
      updateSelectedLegendItems: checkedItems.updateSelectedLegendItems,
      localPerformanceDateRangeRef: session.localPerformanceDateRangeRef,
      localBinsRef: session.localBinsRef,
      updateField: binSettings.updateField,
      defaultBinSettings: defaults?.binSettings,
      setFocusSearch: legend.setFocusSearch
    });

  // Collapsed Others members have no row, so they don't count toward all-checked.
  const isAllChecked =
    checkedItems.checkedLegendItemsRef.current?.filter(
      (item) => !legend.collapsedCheckedItemsRef.current.includes(item)
    ).length === legend.legendItems?.length;

  const { performBatchOption: performCheckedBatchOption } = checkedItems;
  const { collapsedCheckedItemsRef } = legend;
  const performBatchOption = useCallback(
    (action: BatchAction) =>
      performCheckedBatchOption(action, collapsedCheckedItemsRef.current),
    [performCheckedBatchOption, collapsedCheckedItemsRef]
  );

  // ---- P6 fix: former render-body dispatch → real useEffect (fixes P6) ----
  // Highest observable-risk change: dispatch now fires after commit instead of
  // during render. hasCheckedItems/hasEmptyBin remain useMemo-derived during
  // render (INV-C4, now inside useLegendController) — only the dispatch side
  // effect moved, and it stays here because it spans legend + deps.
  const { showGroupsNotInFilter, dispatchUpdateShowGroupsNotInFilter } = deps;
  useEffect(() => {
    if (!legend.hasCheckedItems && !showGroupsNotInFilter && !legend.hasEmptyBin) {
      dispatchUpdateShowGroupsNotInFilter(true);
    }
  }, [
    legend.hasCheckedItems,
    showGroupsNotInFilter,
    legend.hasEmptyBin,
    dispatchUpdateShowGroupsNotInFilter
  ]);

  const { buildMemberItem } = legend;
  const { checkedLegendItemsRef } = checkedItems;
  const { lockBinsToFit, toggleLockBins } = binSettings;
  const checkLegendItem = useCallback(
    (val: boolean, item: LegendItemModel) => {
      item.isChecked = val;
      item.isNotEqual = false;
      item.normalizeBySettings = Object.assign({}, deps.normalizeBy, {
        useNormalizeBy: deps.useNormalizeBy
      });
      if (val) {
        const otherCheckedBins = checkedBinCount(
          checkedLegendItemsRef.current.filter((x) => x.value !== item.value),
          item.property
        );
        // Locks before the filter is sent; a raise lands with the filter's new id.
        lockBinsToFit(otherCheckedBins + (item.isOthersRollup ? 0 : 1));
      }
      checkedItems.addItemToChecked(item);
      if (
        deps.savedFilters.length > 0 &&
        savedFiltersController.isSystemDefaultFilterSelected
      ) {
        savedFiltersController.onSelectedFilterChanged(deps.savedFilters[0]);
      }
      if (!val && !deps.groupBy.bin?.IsLocked) {
        toggleLockBins(true);
      }
    },
    [
      deps,
      checkedItems,
      checkedLegendItemsRef,
      savedFiltersController,
      lockBinsToFit,
      toggleLockBins
    ]
  );

  const handleLegendCheckChange = useCallback(
    () => (val: boolean, item: LegendItemModel) => checkLegendItem(val, item),
    [checkLegendItem]
  );

  // Values picked from Others, per focus field. Bin Settings stay locked while one is
  // still checked on the focus or Max Bins is raised: a Min count or criteria change
  // would fold a small pick back into a rollup.
  const [othersPicks, setOthersPicks] = useState<Record<string, ReadonlySet<string>>>({});
  const focusProperty = deps.groupBy.property;
  const checkedOnFocus = useMemo(
    () => checkedValueTitles(deps.propertiesFilter, focusProperty),
    [deps.propertiesFilter, focusProperty]
  );
  const isBinSettingsLocked =
    binSettings.isMaxBinsRaised ||
    [...(othersPicks[focusProperty] ?? [])].some((title) => checkedOnFocus.has(title));
  // Max Bins follows the checked values (lockBinsToFit) until every value is
  // unchecked, then goes back to its value before any raise.
  // Read off the live filter, not the legend, which keeps its rows until its filter lands:
  // the restore has to go out with the uncheck's own filter.
  const { restoreMaxBins } = binSettings;
  const hasChecksOnFocus = checkedOnFocus.size > 0;
  useEffect(() => {
    if (hasChecksOnFocus) return;
    restoreMaxBins();
    // Only when the last check clears: a raise renews restoreMaxBins before its
    // check reaches the filter, and rerunning then would undo the raise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasChecksOnFocus]);

  const selectOthersMember = useCallback(
    (member: { title: string; count: number }) => {
      checkLegendItem(true, buildMemberItem(member));
      setOthersPicks((picks) => ({
        ...picks,
        [focusProperty]: new Set(picks[focusProperty]).add(member.title)
      }));
    },
    [buildMemberItem, checkLegendItem, focusProperty]
  );

  const handleFilterToggle = useCallback(
    (isOpen: boolean) => entities.setIsActiveFilterVisible(isOpen),
    [entities]
  );
  const handleLegendToggle = useCallback(
    (isOpen: boolean) => entities.setIsLegendVisible(isOpen),
    [entities]
  );

  return {
    entityKind,
    deps,
    isWell: deps.isWell,
    selectedProjectId,
    projectPermissions,
    selectedProject,

    isActiveFilterVisible: entities.isActiveFilterVisible,
    handleFilterToggle,
    isLegendVisible: entities.isLegendVisible,
    handleLegendToggle,
    isBatchOperationsOpen: entities.isBatchOperationsOpen,
    setIsBatchOperationsOpen: entities.setIsBatchOperationsOpen,
    showTreeMap: entities.showTreeMap,
    setShowTreeMap: entities.setShowTreeMap,
    showGroupBy: entities.showGroupBy,
    groupModePopoverOpen: entities.groupModePopoverOpen,
    setGroupModePopoverOpen: entities.setGroupModePopoverOpen,

    legendTitle: legend.legendTitle,
    legendDisplayTitle: legend.legendDisplayTitle,
    focusSearch: legend.focusSearch,
    setFocusSearch: legend.setFocusSearch,
    isFocusSearchPending: legend.isSearchPending,
    legendItems: legend.legendItems,
    filteredItems: legend.filteredItems,
    selectedLegendItems: checkedItems.selectedLegendItems,
    checkedLegendItems: checkedItems.checkedLegendItemsRef,
    ContextMenuItemsComponent: checkedItems.ContextMenuItemsComponent,
    updateSelectedLegendItems: checkedItems.updateSelectedLegendItems,
    colorLockItem: colorLock.colorLockItem,
    handleLegendCheckChange,
    selectOthersMember,
    isBinSettingsLocked,
    hasCheckedItems: legend.hasCheckedItems,
    hasEmptyBin: legend.hasEmptyBin,
    isAllChecked,
    performBatchOption,
    flushFilteredItems: legend.flushFilteredItems,
    addSelectedLegendItemsToFilter: checkedItems.addSelectedLegendItemsToFilter,

    bin: binSettings.bin,
    canEditBin: binSettings.canEditBin,
    updateBin: binSettings.updateBin,
    updateGroupByBin: binSettings.updateGroupByBin,
    toggleLockBins: binSettings.toggleLockBins,
    resetGroupByBin: binSettings.resetGroupByBin,
    minCountWarningVisible: binSettings.minCountWarningVisible,
    groupByPropertyName: deps.groupBy.property,
    placeholderBinsEnabled: binSettings.placeholderBinsEnabled,
    togglePlaceholderBinsEnabled: binSettings.togglePlaceholderBinsEnabled,

    onActiveFilterNodeRemoved: activeFilter.onActiveFilterNodeRemoved,
    onActiveFilterDoubleClick: activeFilter.onActiveFilterDoubleClick,
    clearAllPropertiesFilter: activeFilter.clearAllPropertiesFilter,

    savedFilters: deps.savedFilters,
    selectedFilter: savedFiltersController.selectedFilter,
    isFilterModified: savedFiltersController.isFilterModified,
    isFiltering: deps.isFiltering,
    onSelectedFilterChanged: savedFiltersController.onSelectedFilterChanged,
    onSaveFilter: savedFiltersController.onSaveFilter,
    handleGroupByChange,
    handlePresetSelection,
    handleRecentFocusItemClick,
    handleFocusSearchKeydown: legend.handleFocusSearchKeydown,

    activePalette: colorLock.activePalette,
    handlePaletteSelection: colorLock.handlePaletteSelection
  };
};
